import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ListChecks, Plus, Trash2, UserPlus, X, ArrowLeft, Mail } from 'lucide-react';
import api from '../lib/api';
import { PageHeader } from '../components/layout/PageHeader';
import { EmptyState } from '../components/layout/EmptyState';
import { ContactPickerDialog } from '../components/contacts/ListDialogs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

const SWATCHES = ['#2F6B57', '#C86B4A', '#2E6F7E', '#6B3F69', '#3A5A40', '#B7791F'];

export default function Lists() {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [active, setActive] = useState(null); // list being viewed

  const load = useCallback(() => {
    setLoading(true);
    api.get('/groups').then((r) => setLists(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

  if (active) return <ListDetail list={active} onBack={() => { setActive(null); load(); }} />;

  return (
    <div data-testid="lists-page">
      <PageHeader title="Lists" subtitle="Save guest list templates you can reuse for any event.">
        <Button onClick={() => setCreateOpen(true)} className="gap-2" data-testid="create-list-button"><Plus className="h-4 w-4" /> New list</Button>
      </PageHeader>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}</div>
      ) : lists.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card">
          <EmptyState icon={ListChecks} title="No lists yet"
            description="Create reusable guest lists (e.g. Family, Close Friends, VIPs) and add or remove people whenever you plan an event."
            action={<Button onClick={() => setCreateOpen(true)} className="gap-2"><Plus className="h-4 w-4" /> New list</Button>} />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {lists.map((l) => (
            <button key={l.id} onClick={() => setActive(l)} className="text-left rounded-2xl border border-border bg-card p-5 hover:-translate-y-0.5 transition-transform shadow-[0_1px_0_rgba(17,24,39,0.04),0_10px_30px_rgba(17,24,39,0.06)]" data-testid="list-card">
              <div className="flex items-center gap-2 mb-3">
                <span className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ background: (l.color || '#2F6B57') + '20' }}><ListChecks className="h-5 w-5" style={{ color: l.color || '#2F6B57' }} /></span>
              </div>
              <h3 className="font-display text-lg font-semibold">{l.name}</h3>
              <p className="text-sm text-muted-foreground mt-1">{(l.contact_ids || []).length} {(l.contact_ids || []).length === 1 ? 'person' : 'people'}</p>
            </button>
          ))}
        </div>
      )}

      <CreateListDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={load} />
    </div>
  );
}

const CreateListDialog = ({ open, onOpenChange, onCreated }) => {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#2F6B57');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Name your list'); return; }
    setSaving(true);
    try {
      await api.post('/groups', { name: name.trim(), color, contact_ids: [] });
      toast.success('List created');
      setName(''); setColor('#2F6B57');
      onCreated();
      onOpenChange(false);
    } catch (e2) { toast.error('Could not create list'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-list-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">New list</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="space-y-1.5"><Label>List name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Family" className="h-11" data-testid="list-name-input" /></div>
          <div className="space-y-1.5">
            <Label>Color</Label>
            <div className="flex gap-2">{SWATCHES.map((c) => <button key={c} type="button" onClick={() => setColor(c)} className={`h-8 w-8 rounded-full border-2 ${color === c ? 'border-foreground' : 'border-transparent'}`} style={{ background: c }} />)}</div>
          </div>
          <DialogFooter><Button type="submit" disabled={saving} className="w-full h-11" data-testid="save-list-button">{saving ? 'Saving…' : 'Create list'}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ListDetail = ({ list, onBack }) => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [meta, setMeta] = useState(list);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/groups/${list.id}/members`).then((r) => { setMembers(r.data.members); setMeta(r.data.list); }).catch(() => {}).finally(() => setLoading(false));
  }, [list.id]);

  useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

  const addMembers = async (ids) => {
    try { await api.post(`/groups/${list.id}/add`, { ids }); toast.success(`Added ${ids.length}`); load(); }
    catch (e) { toast.error('Could not add'); }
  };
  const removeMember = async (id) => {
    try { await api.post(`/groups/${list.id}/remove`, { ids: [id] }); load(); }
    catch (e) { toast.error('Could not remove'); }
  };
  const deleteList = async () => {
    try { await api.delete(`/groups/${list.id}`); toast.success('List deleted'); onBack(); }
    catch (e) { toast.error('Could not delete'); }
  };

  return (
    <div data-testid="list-detail-page">
      <Button variant="ghost" onClick={onBack} className="gap-2 mb-4 -ml-2"><ArrowLeft className="h-4 w-4" /> All lists</Button>
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: (meta.color || '#2F6B57') + '20' }}><ListChecks className="h-6 w-6" style={{ color: meta.color || '#2F6B57' }} /></span>
          <div>
            <h1 className="font-display text-3xl font-semibold">{meta.name}</h1>
            <p className="text-sm text-muted-foreground">{members.length} {members.length === 1 ? 'person' : 'people'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setPickerOpen(true)} className="gap-2" data-testid="add-members-button"><UserPlus className="h-4 w-4" /> Add people</Button>
          <Button variant="ghost" onClick={() => setDelOpen(true)} className="gap-2 text-destructive" data-testid="delete-list-button"><Trash2 className="h-4 w-4" /> Delete</Button>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        {loading ? <div className="p-4 space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 rounded-lg" />)}</div> :
          members.length === 0 ? (
            <EmptyState icon={UserPlus} title="No one in this list yet" description="Add people from your contacts to build this guest list."
              action={<Button onClick={() => setPickerOpen(true)} className="gap-2"><UserPlus className="h-4 w-4" /> Add people</Button>} />
          ) : (
            <table className="w-full">
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid="list-member-row">
                    <td className="px-4 py-3"><div className="text-sm font-medium">{`${m.first_name} ${m.last_name}`.trim() || m.email}</div><div className="text-xs text-muted-foreground">{m.email}</div></td>
                    <td className="px-4 py-3 text-right"><Button size="icon" variant="ghost" onClick={() => removeMember(m.id)} data-testid="remove-member-button"><X className="h-4 w-4" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      <ContactPickerDialog open={pickerOpen} onOpenChange={setPickerOpen} excludeIds={members.map((m) => m.id)} onPick={addMembers} />
      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-xl">Delete this list?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This only deletes the list. Your contacts stay safe.</p>
          <DialogFooter className="gap-2">
            <Button variant="secondary" onClick={() => setDelOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={deleteList} data-testid="confirm-delete-list">Delete list</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
